import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Space, Breadcrumb, Button, Input, Switch, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import type { ColumnsType } from "antd/es/table";

import type {
  DeviceProfileDeviceListItem,
  ListDeviceProfileDevicesResponse,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import {
  GetDeviceProfileDeviceRequest,
  ListDeviceProfileDevicesRequest,
  DeleteDeviceProfileVendorRequest,
  UpdateDeviceProfileDeviceRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import type { DeviceProfileVendor } from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";

import type { GetPageCallbackFunc } from "../../components/DataTable";
import DataTable from "../../components/DataTable";
import deviceProfileStore from "../../stores/DeviceProfileStore";
import { useTitle } from "../helpers";
import DeleteConfirm from "../../components/DeleteConfirm";

interface IProps {
  vendor: DeviceProfileVendor;
}

function ListDeviceProfileDevices(props: IProps) {
  const navigate = useNavigate();
  useTitle(["Network Server", "Device Profiles", "Vendors", props.vendor.getName()]);
  const [refreshKey, setRefreshKey] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const isBridge = props.vendor.getSource() === "bridge";

  // See ListVendors.togglePublished — same pattern, get-then-update so the
  // backend has a full payload to merge against.
  const togglePublished = (id: string, next: boolean) => {
    setTogglingId(id);
    const getReq = new GetDeviceProfileDeviceRequest();
    getReq.setId(id);
    deviceProfileStore.getDevice(getReq, resp => {
      const d = resp.getDevice();
      if (!d) {
        setTogglingId(null);
        return;
      }
      d.setPublished(next);
      const updReq = new UpdateDeviceProfileDeviceRequest();
      updReq.setDevice(d);
      deviceProfileStore.updateDevice(updReq, () => {
        setTogglingId(null);
        setRefreshKey(k => k + 1);
      });
    });
  };

  const columns: ColumnsType<DeviceProfileDeviceListItem.AsObject> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Link to={`/device-profiles/vendors/${props.vendor.getId()}/devices/${record.id}/profiles`}>{text}</Link>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 120,
      render: source =>
        source === "repo" ? <Tag color="blue">Repo</Tag> : <Tag color="green">Bridge</Tag>,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      width: 120,
      render: (_published, record) => (
        <Switch
          checked={record.published}
          loading={togglingId === record.id}
          onChange={next => togglePublished(record.id, next)}
        />
      ),
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    _filters: object,
    _orderBy: string | void,
    _orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    const req = new ListDeviceProfileDevicesRequest();
    req.setVendorId(props.vendor.getId());
    req.setLimit(limit);
    req.setOffset(offset);
    req.setSearch(search);

    deviceProfileStore.listDevices(req, (resp: ListDeviceProfileDevicesResponse) => {
      const obj = resp.toObject();
      callbackFunc(obj.totalCount, obj.resultList);
    });
  };

  const deleteVendor = () => {
    const req = new DeleteDeviceProfileVendorRequest();
    req.setId(props.vendor.getId());

    deviceProfileStore.deleteVendor(req, () => {
      navigate("/device-profiles/vendors");
    });
  };

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb
            items={[
              { title: "Network Server" },
              { title: "Device Profiles" },
              { title: <Link to={`/device-profiles/vendors`}>Vendors</Link> },
              { title: props.vendor.getName() },
            ]}
          />
        )}
        title={props.vendor.getName()}
        tags={
          props.vendor.getSource() === "repo" ? (
            <Tag color="blue">Repo</Tag>
          ) : (
            <Tag color="green">Bridge</Tag>
          )
        }
        extra={[
          <Input.Search
            key="search-devices"
            placeholder="Search by name"
            allowClear
            onSearch={value => {
              setSearch(value);
              setRefreshKey(k => k + 1);
            }}
            style={{ width: 240 }}
          />,
          <Space key="add-device-edit-delete-vendor">
            <Button type="primary" onClick={() => navigate(`/device-profiles/vendors/${props.vendor.getId()}/devices/create`)}>
              Add device
            </Button>
            {isBridge && (
              <Button onClick={() => navigate(`/device-profiles/vendors/${props.vendor.getId()}/edit`)}>
                Edit vendor
              </Button>
            )}
            <DeleteConfirm confirm={props.vendor.getName()} typ="vendor" onConfirm={deleteVendor}>
              <Button danger type="primary">
                Delete vendor
              </Button>
            </DeleteConfirm>
          </Space>,
        ]}
      />
      <DataTable columns={columns} getPage={getPage} rowKey="id" refreshKey={refreshKey} />
    </Space>
  );
}

export default ListDeviceProfileDevices;
